import { FC, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  getGetPlanningsQueryKey,
  useGetPlannings,
  useUploadPlanningCsv,
} from '@/api/generated';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { formatDateForDisplay } from '@/lib/utils';
import CsvUploadButton from '@/routes/(app)/(scenarios)/(create)/components/csv-upload-button';
import SourceOptionList from '@/routes/(app)/(scenarios)/(create)/components/source-option-list';
import { getLatestUploadId } from '@/routes/(app)/(scenarios)/(create)/create-scenario.utils';
import { CreateScenarioSchema } from '@/routes/(app)/(scenarios)/(create)/types';

interface ShipmentPlanningStepProps {
  disabled: boolean;
}

const ShipmentPlanningStep: FC<ShipmentPlanningStepProps> = ({ disabled }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: uploadPlanningCsv, isPending: isUploadingPlanningCsv } =
    useUploadPlanningCsv();
  const { data, isLoading } = useGetPlannings({ skip: 0, limit: 15 });

  const plannings = useMemo(() => data?.items ?? [], [data]);
  const latestPlanningId = useMemo(
    () => getLatestUploadId(plannings),
    [plannings]
  );
  const options = useMemo(
    () =>
      plannings.flatMap((planning) =>
        planning.id
          ? [
              {
                value: planning.id,
                title:
                  planning.filename ??
                  t('scenarios.create.uploadPlanning.untitled'),
                description: t(
                  'scenarios.create.uploadPlanning.optionDescription',
                  {
                    date: formatDateForDisplay(planning.upload_timestamp),
                    count: planning.ship_visits?.length ?? 0,
                  }
                ),
                isLatest: planning.id === latestPlanningId,
              },
            ]
          : []
      ),
    [plannings, latestPlanningId, t]
  );
  const form = useFormContext<CreateScenarioSchema>();
  const { planningId } = form.watch();

  // Pre-selected once a yard is chosen, matching the yard step.
  useEffect(() => {
    if (disabled || planningId || !latestPlanningId) return;
    form.setValue('planningId', latestPlanningId);
  }, [disabled, planningId, latestPlanningId, form]);

  const handleUploadPlanningCsv = useCallback(
    async (file: File) => {
      try {
        const response = await uploadPlanningCsv({ data: { file } });
        await queryClient.invalidateQueries({
          queryKey: getGetPlanningsQueryKey(),
        });
        form.setValue('planningId', response.id ?? '');
      } catch {
        toast.error(t('general.toast.somethingWentWrong'));
      }
    },
    [uploadPlanningCsv, queryClient, form, t]
  );

  return (
    <Card>
      <CardHeader className='flex flex-row items-start justify-between gap-3'>
        <div className='flex flex-col gap-1.5'>
          <CardTitle>{t('scenarios.create.uploadPlanning.title')}</CardTitle>
          <CardDescription>
            {t('scenarios.create.uploadPlanning.description')}
          </CardDescription>
        </div>
        <CsvUploadButton
          onFile={handleUploadPlanningCsv}
          isUploading={isUploadingPlanningCsv}
          disabled={disabled}
        />
      </CardHeader>
      <CardContent>
        <SourceOptionList
          label={t('scenarios.create.uploadPlanning.title')}
          options={options}
          value={planningId}
          onValueChange={(value) => {
            form.setValue('planningId', value);
          }}
          isLoading={isLoading}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
};

export default ShipmentPlanningStep;
